import { useCallback } from 'react';
import type { ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { VStack } from '@cbhq/cds-web/layout';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import type { ErrorMetadata } from '@cbhq/error-vitals-web';
import { ErrorBoundary } from '@cbhq/error-vitals-web';

import { TeamErrorFallback } from '../components/ErrorBoundary/TeamErrorFallback';
import { useOrganization } from '../contexts/OrganizationContext';
import { EditMember } from '../pages/Team/pages/EditMember';
import { Invite } from '../pages/Team/pages/Invite';
import { Members } from '../pages/Team/pages/Members';
import { ROUTE_TEAM } from '../pages/Team/utils/constants';

const TEAM_ERROR_BOUNDARY_DEFAULT_METADATA: ErrorMetadata = {
  context: 'error-boundary',
  severity: 'error',
  isHandled: true,
};

function TeamRoutes(): ReactElement | null {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;

  return (
    <>
      {/*
       * Background location pattern for Team overlays:
       *
       * First <Routes> uses backgroundLocation when present, so the members list
       * stays mounted behind the overlay during normal navigation. The explicit
       * "member/:user_uuid" fallback here renders the list when the user navigates
       * directly to the URL (no backgroundLocation in history state, e.g. page refresh).
       *
       * Second <Routes> always matches the current URL and renders the foreground overlay.
       * Do NOT add other routes to the second block — they would appear on top of the list.
       */}
      <Routes location={backgroundLocation || location}>
        <Route index element={<Members />} />
        <Route path="member/:user_uuid" element={<Members />} />
        <Route path="invite" element={<Members />} />
        <Route path="*" element={<Navigate to={ROUTE_TEAM} replace />} />
      </Routes>
      <Routes>
        <Route path="member/:user_uuid" element={<EditMember />} />
        <Route path="invite" element={<Invite />} />
      </Routes>
    </>
  );
}

export default function Team(): ReactElement | null {
  const { selectedOrganization } = useOrganization();
  const handleCatch = useCallback((error: Error) => {
    logEvent('team_management_error', {
      action: ActionType.measurement,
      componentType: ComponentType.page,
      error: error.message,
    });
  }, []);

  return (
    <VStack width="100%" height="100%">
      <ErrorBoundary
        // Remount the boundary when the org changes so a stale cached error
        // for the previous org doesn't keep the fallback stuck after switching.
        key={selectedOrganization?.organizationId}
        name="team-boundary"
        defaultMetadata={TEAM_ERROR_BOUNDARY_DEFAULT_METADATA}
        fallback={<TeamErrorFallback />}
        onCatch={handleCatch}
      >
        <TeamRoutes />
      </ErrorBoundary>
    </VStack>
  );
}
